// ============================================================================
// STUDIO CANETTE — LE THÈME, ET POURQUOI IL N'Y EN A PLUS QU'UN
//
// Le produit a longtemps proposé clair / sombre / système. LE SOMBRE EST
// RETIRÉ : la palette de l'application est un crème et un jaune francs, et sa
// transposition en sombre ne rendait pas — un poste de travail où l'on juge des
// couleurs de packaging n'a d'ailleurs rien à gagner à changer de luminosité
// sous l'objet qu'on regarde. Le fond NOIR reste disponible là où il a un sens,
// c'est-à-dire dans la scène 3D, en tant que fond de scène (`setBg`) et non en
// tant que thème d'interface.
//
// CE MODULE GARDE SON API ENTIÈRE, et c'est volontaire : `setTheme()`,
// `onTheme()`, `effectiveTheme()` sont appelés par les deux moteurs, la
// visionneuse et la coquille. Les neutraliser ICI coûte dix lignes ; les
// retirer partout en coûterait cent et laisserait des appels morts dans du code
// qu'on relit rarement. Le jour où un thème sombre présentable existe, tout
// revient d'ici : `getTheme`, `effectiveTheme`, `setTheme` et le bouton.
//
// `themeToggleButton()` rend `null` : `h()` ignore les enfants nuls, donc les
// six en-têtes qui l'appelaient n'ont pas bougé d'une ligne.
// ============================================================================

import { h } from './dom.js';

/** La clé d'hier : on ne l'écrit plus, on l'efface (voir `applyToDocument`). */
const KEY = 'sc_theme';
const listeners = new Set();

/** Le thème choisi. Toujours 'light' : il n'y a plus que celui-là. */
export function getTheme() { return 'light'; }

/** Ce qui est réellement affiché. */
export const effectiveTheme = () => 'light';

function applyToDocument() {
  const root = document.documentElement;
  // Posé EXPLICITEMENT, et non retiré : sans l'attribut, tokens.css repasserait
  // sur `prefers-color-scheme: dark` chez un visiteur dont le système est en
  // sombre — et le sombre est justement ce qu'on ne veut plus.
  root.dataset.theme = 'light';
  // On efface la préférence d'hier : un compte qui avait choisi « sombre »
  // rouvrirait en sombre avant même que ce module ne tourne (voir le script
  // synchrone d'index.html).
  try { localStorage.removeItem(KEY); } catch { /* navigation privée */ }

  // La barre d'adresse mobile prend la couleur du thème. On lit le jeton calculé
  // plutôt qu'une valeur en dur : la palette n'a qu'une source, tokens.css.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const value = getComputedStyle(root).getPropertyValue('--yellow').trim();
    if (value) meta.setAttribute('content', value);
  }
}

function emit() {
  const payload = { mode: getTheme(), theme: effectiveTheme() };
  for (const fn of [...listeners]) {
    try { fn(payload); } catch (e) { console.error('[theme]', e); }
  }
}

/**
 * Pose un thème — sans effet, il n'y en a qu'un.
 *
 * Gardée pour les appelants : la visionneuse demande encore `setTheme('light')`
 * en posant un fond, et ce n'est pas faux, c'est simplement déjà le cas.
 */
export function setTheme() {
  applyToDocument();
  return 'light';
}

/** Sans objet : un seul thème. */
export function cycleTheme() { return 'light'; }

/**
 * S'abonne aux changements. Appelle tout de suite avec l'état courant.
 * Le studio 3D en a besoin : la couleur de fond du canvas n'est pas du CSS.
 * @returns {() => void}
 */
export function onTheme(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  try { fn({ mode: getTheme(), theme: effectiveTheme() }); } catch (e) { console.error('[theme]', e); }
  return () => listeners.delete(fn);
}

let started = false;

/** À appeler une fois, au démarrage de l'application. */
export function initTheme() {
  if (started) return;
  started = true;
  ensureStyles();
  applyToDocument();
  emit();
}

/**
 * Le bouton de bascule — plus de bascule, donc plus de bouton.
 *
 * Rend `null` plutôt que d'être supprimé de ses six appelants : `h()` ignore
 * les enfants nuls, et le jour où un thème sombre présentable existe, tout
 * revient d'un seul fichier.
 */
export function themeToggleButton() { return null; }

/* Styles du bouton — le seul markup produit par ce module. Uniquement des
   jetons : aucune couleur littérale. */
function ensureStyles() {
  if (document.getElementById('sc-theme-css')) return;
  document.head.appendChild(h('style#sc-theme-css', {
    text: `
.sc-iconbtn{display:inline-flex;align-items:center;justify-content:center;
  width:36px;height:36px;padding:0;cursor:pointer;color:var(--ink);
  background:var(--surface);border:var(--bd);border-radius:var(--r);
  box-shadow:var(--sh-xs);
  transition:transform var(--t-fast) var(--ease),box-shadow var(--t-fast) var(--ease)}
.sc-iconbtn:hover{transform:translate(var(--lift),var(--lift));box-shadow:var(--sh-sm);background:var(--acc)}
.sc-iconbtn:active{transform:translate(var(--press),var(--press));box-shadow:none}
.sc-iconbtn:focus-visible{outline:var(--bd-w-lg) solid var(--focus);outline-offset:2px}
.sc-iconbtn svg{fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.sc-iconbtn svg .fill{fill:currentColor;stroke:none}
`
  }));
}
